import {
  AnimationTypes,
  DEFAULT_TEXTURES,
  DefaultCharacterTextures,
  FrameConfiguration,
} from '@/common/types/game';
import { Customization, EquipmentConfiguration } from '@/common/types/student';
import Customizations from '@/game/GameComponents/GameData/customizations.json';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import { getCharacterInventory } from '@/game/GameComponents/PhaserGame/GameInstance';
import EventsManager from '@/game/GameComponents/PhaserGame/Managers/EventsManager';
import {
  AnimationUtils,
  Direction,
} from '@/game/GameComponents/PhaserGame/Utils/AnimationUtils';
import {
  getCustomizationFromId,
  getSpriteSheetPath,
  hasLoadedCustomizationTextures,
  loadCustomizationTextures,
} from '@/game/GameComponents/PhaserGame/Utils/CosmeticUtils';
import DynamicSortByY from '@/game/GameComponents/PhaserGame/Utils/DynamicSortByY';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { performSlotHiding } from '@/game/GameComponents/PhaserGame/Utils/NpcUtils';
import {
  getDistanceBetweenPoints,
  waitForSeconds,
  waitUntil,
} from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import {
  Reward,
  RewardType,
} from '@/game/GameComponents/PhaserGame/Utils/RewardsManager';
import { getGameName } from '@/game/gameHelpers';
import WorldNode from '@/game/prefabs/World/WorldNode';
import WorldRewardFeedback from '@/game/prefabs/World/WorldRewardFeedback';
import DailyProgressBarWorld from '@/game/prefabs/WorldUI/DailyProgressBarWorld';
import Portal from './Portal';

/* START OF COMPILED CODE */

export default class Player extends Phaser.GameObjects.Container {
  constructor(scene: Phaser.Scene, x?: number, y?: number) {
    super(scene, x ?? 0, y ?? 0);

    // player_complexion
    const player_complexion = scene.add.sprite(0, 0, 'complexion-1', 0);
    player_complexion.name = 'player_complexion';
    player_complexion.setOrigin(0.5, 0.69);
    this.add(player_complexion);

    // player_pants
    const player_pants = scene.add.sprite(0, 0, 'blue-frilly-skirt', 0);
    player_pants.name = 'player_pants';
    player_pants.setOrigin(0.5, 0.69);
    this.add(player_pants);

    // player_clothes
    const player_clothes = scene.add.sprite(0, 0, 'blue-tank-top', 0);
    player_clothes.name = 'player_clothes';
    player_clothes.setOrigin(0.5, 0.69);
    this.add(player_clothes);

    // player_back
    const player_back = scene.add.sprite(0, 0, 'grey-cape', 0);
    player_back.name = 'player_back';
    player_back.setOrigin(0.5, 0.69);
    this.add(player_back);

    // player_shoes
    const player_shoes = scene.add.sprite(0, 0, 'brown-shoes', 0);
    player_shoes.name = 'player_shoes';
    player_shoes.setOrigin(0.5, 0.69);
    this.add(player_shoes);

    // player_gloves
    const player_gloves = scene.add.sprite(0, 0, 'blue-boxing-gloves', 0);
    player_gloves.name = 'player_gloves';
    player_gloves.setOrigin(0.5, 0.69);
    this.add(player_gloves);

    // player_hair
    const player_hair = scene.add.sprite(0, 0, 'brown-bob', 0);
    player_hair.name = 'player_hair';
    player_hair.setOrigin(0.5, 0.69);
    this.add(player_hair);

    // player_face
    const player_face = scene.add.sprite(0, 0, 'grey-friendly-fella', 0);
    player_face.name = 'player_face';
    player_face.setOrigin(0.5, 0.69);
    this.add(player_face);

    // player_hats
    const player_hats = scene.add.sprite(0, 0, 'straw-hat', 0);
    player_hats.name = 'player_hats';
    player_hats.setOrigin(0.5, 0.69);
    this.add(player_hats);
    // awake handler
    this.scene.events.once('scene-awake', () => this.awake());

    /* START-USER-CTR-CODE */
    this.state = 'travelling';
    this.body = null;
    this.bodyTexture = '';
    /* END-USER-CTR-CODE */
  }

  public interactionEnabled: boolean = true;

  /* START-USER-CODE */

  state: 'attacking' | 'travelling';
  body: Phaser.Physics.Arcade.Body | null = null;
  bodyTexture: string;
  customizationAssetConfiguration: any;
  moveToX: number | null = null;
  moveToY: number | null = null;
  targetObject: WorldNode | Portal | Phaser.GameObjects.Container | null = null;
  prevTargetObject?: WorldNode | Portal | Phaser.GameObjects.Container | null =
    null;
  CHAR_MOVEMEMENT_SPEED: number = 150;
  onNodeReachedCallback?: (
    node: WorldNode | Portal | Phaser.GameObjects.Container,
  ) => void;
  onNodeLeftCallback?: (
    node: WorldNode | Portal | Phaser.GameObjects.Container,
  ) => void;
  keyboardInput: any;
  hasFinishedLoadingCosmetics: boolean = false;

  async preloadCustomizations() {
    // Load customization assets.
    const inventory = getCharacterInventory();
    const items = inventory.equippedItems as any;

    for (var i = 0; i < Object.keys(items).length; i++) {
      const key = Object.keys(items)[i];
      const customization = getCustomizationFromId(
        items[key as keyof EquipmentConfiguration],
      );
      if (
        customization !== undefined &&
        !hasLoadedCustomizationTextures(this.scene, customization)
      ) {
        await new Promise((resolve) =>
          loadCustomizationTextures(this.scene, customization, () => {
            resolve(true);
          }),
        );
      }
    }
    EventBus.emit(GAME_EVENTS.PLAYER_TEXTURES_FINISHED_REFRESH);

    EventBus.emit(GAME_EVENTS.PLAYER_CUSTOMIZATION_ASSETS_LOADED);
    this.hasFinishedLoadingCosmetics = true;
  }

  awake() {
    this.setVisible(false);
    this.body = this.scene.physics.add.body(this.x, this.y, 5, 5);
    this.body.collideWorldBounds = true;
    this.body.onWorldBounds = true;

    this.bodyTexture = this.getCharacterBodyTexture(); // The default body texture.
    this.keyboardInput = this.scene.input.keyboard?.createCursorKeys();

    EventsManager.registerEventOn(
      this.scene,
      GAME_EVENTS.PLAYER_CLAIMED_REWARD,
      this.handleClaimedReward,
      this,
    );
    EventsManager.registerEventOn(
      this.scene,
      GAME_EVENTS.MASTERED_NEW_MINIGAME,
      this.handleMinigameMastered,
      this,
    );
    EventsManager.registerEventOn(
      this.scene,
      GAME_EVENTS.PLAYER_EQUIPPED_COSMETIC,
      this.handleEquippedCosmetic,
      this,
    );
    EventsManager.registerEventOn(
      this.scene,
      GAME_EVENTS.PLAYER_UNEQUIPPED_COSMETIC,
      this.handleEquippedCosmetic,
      this,
    );
    this.scene.input.on('pointerdown', this.handlePointerDown, this);
    this.scene.events.on('prerender', this.handlePrerender, this);
    this.scene.events.on('update', this.update, this);
    this.scene.events.once('create', this.handleSceneReady, this);

    // Hide the player until the customizations are loaded.
    this.preloadCustomizations();
  }

  setTargetNode(node: WorldNode | Portal | Phaser.GameObjects.Container) {
    // TODO: decouple. Only useful in world map.
    this.targetObject = node;
  }

  updateCharacterAppearance() {
    const equippedItems = getCharacterInventory().equippedItems;
    this.list.forEach((child: any) => {
      if (child.name !== 'player_complexion') {
        child.setVisible(false);
      }
    });

    Object.keys(equippedItems).forEach((key: string) => {
      const customizationId =
        equippedItems[key as keyof EquipmentConfiguration];
      if (customizationId !== null) {
        const customizationData = Customizations.find(
          (customization) => customization.id === customizationId,
        ) as Customization;
        if (!customizationData) return;
        const customizationPath =
          getSpriteSheetPath(customizationData) ??
          DEFAULT_TEXTURES[key as keyof DefaultCharacterTextures]?.spritesheet;

        // Find the child game object with the corresponding slot.
        const slotObj = this.list.find(
          (child) => child.name === `player_${customizationData?.slot}`,
        );
        const slotSprite = slotObj as Phaser.GameObjects.Sprite;
        if (slotSprite as Phaser.GameObjects.Sprite) {
          slotSprite.setVisible(true);
          slotSprite.setTexture(customizationPath);
        }
      } else {
        // FALLBACKS FOR CLOTHES AND PANTS.
        if (key === 'clothes' || key === 'pants') {
          const slotObj = this.list.find(
            (child) => child.name === `player_${key}`,
          );
          const slotSprite = slotObj as Phaser.GameObjects.Sprite;
          if (slotSprite as Phaser.GameObjects.Sprite) {
            slotSprite.setVisible(true);
            slotSprite.setTexture(
              key === 'clothes' ? 'blue-loose-shirt-m' : 'grey-long-pants',
            );
          }
        }
      }
    });

    // Slot hiding step.
    performSlotHiding(
      this,
      equippedItems,
      'player_',
      Customizations as Customization[],
    );
  }

  update() {
    const character = this.body;

    if (this.interactionEnabled) {
      // If the player is using the keyboard to move, handle that and return out of the update loop.
      if (this.handleKeyboardInput(character!)) {
        return;
      }

      // Perform mouse movement.

      let distance = 0;

      if (this.moveToX && this.moveToY) {
        distance = getDistanceBetweenPoints(
          new Phaser.Math.Vector2(this.moveToX, this.moveToY),
          new Phaser.Math.Vector2(character!.x, character!.y),
        );

        // Check leaving the previous target.
        if (this.prevTargetObject) {
          const distanceFromTarget = getDistanceBetweenPoints(
            new Phaser.Math.Vector2(
              this.prevTargetObject.x,
              this.prevTargetObject.y,
            ),
            new Phaser.Math.Vector2(character!.x, character!.y),
          );

          if (distanceFromTarget > 20) {
            this.onNodeLeft(this.prevTargetObject);
          }
        }
      }

      if (this.targetObject !== null) {
        // Calculate distance to the object, rather than the point.

        distance = getDistanceBetweenPoints(
          new Phaser.Math.Vector2(this.targetObject.x, this.targetObject.y),
          new Phaser.Math.Vector2(character!.x, character!.y),
        );
      }

      // Calculate if we have reached the target.
      let hasReachedTarget = distance < 4;
      if (this.targetObject !== null) {
        hasReachedTarget = distance < 20;
      }

      if (!hasReachedTarget) {
        // Keep moving if we haven't reached the target.
        const targetX = this.targetObject?.x || this.moveToX!;
        const targetY = this.targetObject?.y || this.moveToY!;

        const angle = Math.atan2(
          targetY - character!.y,
          targetX - character!.x,
        );

        this.handleMovementAndAnimation(character!, angle);
      } else {
        // Stop moving and interact with the target object if it is set.
        character!.setVelocityX(0);
        character!.setVelocityY(0);
        AnimationUtils.setIdle(this, Direction.DOWN);

        this.moveToX = null;
        this.moveToY = null;
        // Handle node.
        if (this.targetObject && !this.prevTargetObject) {
          this.onNodeReached(this.targetObject);
        }
        this.targetObject = null;
      }
    } else {
      character!.setVelocityX(0);
      character!.setVelocityY(0);
    }
  }

  getInteractionPoint(
    target: WorldNode | Portal | Phaser.GameObjects.Container,
    character: Phaser.Physics.Arcade.Body,
  ) {
    const targetVector = new Phaser.Math.Vector2(target.x, target.y);
    const characterVector = new Phaser.Math.Vector2(character.x, character.y);
    const distance = getDistanceBetweenPoints(targetVector, characterVector);

    if (distance < 20) {
      return characterVector;
    }

    return null;
  }

  handlePointerDown(pointer: Phaser.Input.Pointer) {
    this.scene.sound.unlock();
    if (!this.interactionEnabled) return;
    const worldPoint = pointer.positionToCamera(
      this.scene.cameras.main,
    ) as Phaser.Math.Vector2;
    this.moveToX = worldPoint.x;
    this.moveToY = worldPoint.y;
    if (this.prevTargetObject) {
      this.onNodeLeftCallback?.(this.prevTargetObject);
    }
    this.prevTargetObject = undefined;
  }

  handleKeyboardInput(character: Phaser.Physics.Arcade.Body) {
    let keyboardX = 0;
    let keyboardY = 0;

    let isKeyboardInput = false;

    if (this.keyboardInput.left.isDown) {
      isKeyboardInput = true;
      keyboardX = -1;
    }
    if (this.keyboardInput.right.isDown) {
      isKeyboardInput = true;
      keyboardX = 1;
    }
    if (this.keyboardInput.up.isDown) {
      isKeyboardInput = true;
      keyboardY = -1;
    }
    if (this.keyboardInput.down.isDown) {
      isKeyboardInput = true;
      keyboardY = 1;
    }

    if (isKeyboardInput) {
      this.moveToX = null;
      this.moveToY = null;
      this.targetObject = null;
      let angle = Math.atan2(keyboardY, keyboardX);
      this.handleMovementAndAnimation(character!, angle);
    }
    return isKeyboardInput;
  }

  handleMovementAndAnimation(
    character: Phaser.Physics.Arcade.Body,
    angle: number,
  ) {
    const movementVector = new Phaser.Math.Vector2(
      Math.cos(angle) * this.CHAR_MOVEMEMENT_SPEED,
      Math.sin(angle) * this.CHAR_MOVEMEMENT_SPEED,
    );
    character.setVelocityX(movementVector.x);
    character.setVelocityY(movementVector.y);

    let direction;
    if (angle > -Math.PI / 4 && angle <= Math.PI / 4) {
      direction = 'right';
      //this.setFlipX(false); // Ensure sprite is not flipped when moving right
    } else if (angle > Math.PI / 4 && angle <= (3 * Math.PI) / 4) {
      direction = 'down';
      //this.sprite.setFlipX(false); // Reset flip to default when moving down
    } else if (
      (angle > (3 * Math.PI) / 4 && angle <= Math.PI) ||
      (angle >= -Math.PI && angle <= (-3 * Math.PI) / 4)
    ) {
      direction = 'left';
      //this.sprite.setFlipX(true); // Flip sprite to face left
    } else {
      direction = 'up';
      //this.sprite.setFlipX(false); // Reset flip to default when moving up
    }

    //this.sprite.anims.play(`${this.bodyTexture}-walk-${direction}`, true);

    // Loop through each customization and play its walking animation if it exists
    AnimationUtils.setWalking(this, this.scene, direction);

    // for (const key in this.customizationAssetConfiguration) {
    //   const customization = this.customizationAssetConfiguration[key];
    //   if (customization && customization.texture.key) {
    //     const animKey = `${customization.texture.key}-walk-${direction}`;
    //     if (this.scene.anims.exists(animKey)) {
    //       customization.anims.play(animKey, true);
    //       if (direction === 'left') {
    //         customization.setFlipX(true); // Ensure customization is flipped when moving left
    //       } else {
    //         customization.setFlipX(false); // Reset flip to default for other directions
    //       }
    //     }
    //   }
    // }
  }

  async handleClaimedReward(reward: Reward) {
    const rewardFeedback = new WorldRewardFeedback(this.scene, this.x, this.y);
    this.scene.add.existing(rewardFeedback);
    rewardFeedback.setReward(reward);
    rewardFeedback.setParent(this);
    await waitForSeconds(this.scene.time, 1.5);
    const progressBarFeedback = new DailyProgressBarWorld(
      this.scene,
      this.x,
      this.y - 30,
    );
    this.scene.add.existing(progressBarFeedback);
    progressBarFeedback.setParent(this);
  }

  handleMinigameMastered(game: string) {
    const rewardFeedbackObj = new WorldRewardFeedback(
      this.scene,
      this.x,
      this.y - 15,
    );
    rewardFeedbackObj.setReward({
      rewardType: RewardType.MINIGAME_MASTERY,
      rubies: 0,
      masteryGame: game,
      rewardCompleteLabel: `${getGameName(game)} Mastered`,
      autoAccept: true,
    });
    rewardFeedbackObj.setParent(this);
    this.scene.add.existing(rewardFeedbackObj);
  }

  onNodeReached(node: WorldNode | Portal | Phaser.GameObjects.Container) {
    if (this.interactionEnabled) {
      if (this.onNodeReachedCallback) {
        this.onNodeReachedCallback(node);
      }
    }

    this.prevTargetObject = this.targetObject;
    this.targetObject = null;
  }

  onNodeLeft(node: WorldNode | Portal | Phaser.GameObjects.Container) {
    if (this.interactionEnabled) {
      if (this.onNodeLeftCallback) {
        this.onNodeLeftCallback(node);
      }
    }
    this.prevTargetObject = undefined;
  }

  // Phaser requires us to load all of our animations before the scene starts.
  // This means we can't dynamically load anims based on which outfits are equipped,
  // and instead must load all possible animations for all customizations before we start our scene.
  // Not ideal but if performance becomes too heavily affected, we could add a quick loading screen.
  createAnimations() {
    const availableComplexions = Customizations.filter(
      (item) => item.slot === 'complexion',
    ) as Customization[];

    // Setup animations for each complexion.
    const baseAnimations: {
      [key: string]: { texture: string; start: number; end: number };
    } = {};
    for (var i = 0; i < availableComplexions.length; i++) {
      const spriteSheetPath = getSpriteSheetPath(availableComplexions[i]);
      baseAnimations[`${spriteSheetPath}-idle`] = {
        texture: spriteSheetPath,
        start: 193,
        end: 194,
      };
      baseAnimations[`${spriteSheetPath}-walk-right`] = {
        texture: spriteSheetPath,
        start: 64,
        end: 69,
      };
      baseAnimations[`${spriteSheetPath}-walk-left`] = {
        texture: spriteSheetPath,
        start: 64,
        end: 69,
      };
      baseAnimations[`${spriteSheetPath}-walk-up`] = {
        texture: spriteSheetPath,
        start: 52,
        end: 54,
      };
      baseAnimations[`${spriteSheetPath}-walk-down`] = {
        texture: spriteSheetPath,
        start: 48,
        end: 50,
      };
      baseAnimations[`${spriteSheetPath}-idle-down`] = {
        texture: spriteSheetPath,
        start: 0,
        end: 0,
      };
      baseAnimations[`${spriteSheetPath}-idle-up`] = {
        texture: spriteSheetPath,
        start: 16,
        end: 16,
      };
      baseAnimations[`${spriteSheetPath}-idle-left`] = {
        texture: spriteSheetPath,
        start: 32,
        end: 32,
      };
      baseAnimations[`${spriteSheetPath}-thumbsup`] = {
        texture: spriteSheetPath,
        start: 243,
        end: 243,
      };
      baseAnimations[`${spriteSheetPath}-wave`] = {
        texture: spriteSheetPath,
        start: 92,
        end: 93,
      };
      baseAnimations[`${spriteSheetPath}-meditate`] = {
        texture: spriteSheetPath,
        start: 15,
        end: 15,
      };
      baseAnimations[`${spriteSheetPath}-sing`] = {
        texture: spriteSheetPath,
        start: 60,
        end: 61,
      };
      baseAnimations[`${spriteSheetPath}-defeated`] = {
        texture: spriteSheetPath,
        start: 245,
        end: 245,
      };
      baseAnimations[`${spriteSheetPath}-hurt`] = {
        texture: spriteSheetPath,
        start: 244,
        end: 244,
      };
      baseAnimations[`${spriteSheetPath}-attack`] = {
        texture: spriteSheetPath,
        start: 136,
        end: 137,
      };
    }

    // Iterate through the baseAnimations to create each one if it doesn't exist
    Object.entries(baseAnimations).forEach(([key, { texture, start, end }]) => {
      if (texture !== '__MISSING') {
        if (this.scene.anims.exists(key)) {
          this.scene.anims.remove(key);
        }

        if (this.scene.textures.exists(texture)) {
          this.scene.anims.create({
            key,
            frames: this.scene.anims.generateFrameNumbers(`${texture}`, {
              start,
              end,
            }),
            frameRate: 10,
            repeat: -1,
          });
        }
      }
    });

    const frameConfigurations: FrameConfiguration = {
      idle: { start: 193, end: 194 },
      'walk-right': { start: 64, end: 69 },
      'walk-left': { start: 64, end: 69 },
      'walk-up': { start: 52, end: 54 },
      'walk-down': { start: 48, end: 50 },
      'idle-down': { start: 0, end: 0 },
      'idle-up': { start: 16, end: 16 },
      'idle-left': { start: 32, end: 32 },
      thumbsup: { start: 243, end: 243 },
      wave: { start: 92, end: 93 },
      meditate: { start: 15, end: 15 },
      sing: { start: 60, end: 61 },
      defeated: { start: 245, end: 245 },
      hurt: { start: 244, end: 244 },
      attack: { start: 136, end: 137 },
    };

    const customizations = Customizations as Customization[];
    customizations.forEach((customization) => {
      const spriteSheetPath = getSpriteSheetPath(customization);
      (Object.keys(frameConfigurations) as AnimationTypes[]).forEach(
        (animType) => {
          const key = `${spriteSheetPath}-${animType}`;
          if (this.scene.anims.exists(key)) {
            this.scene.anims.remove(key);
          }
          const frameNumbers = frameConfigurations[animType];
          if (this.scene.textures.exists(spriteSheetPath)) {
            this.scene.anims.create({
              key,
              frames: this.scene.anims.generateFrameNumbers(
                spriteSheetPath,
                frameNumbers,
              ),
              frameRate: 10,
              repeat: -1,
            });
          }
        },
      );
    });

    // Setup callback for animation events.
    this.list.forEach((child: any) => {
      if (child instanceof Phaser.GameObjects.Sprite) {
        child.on('animationrepeat', (animation: any) => {
          if (
            animation.key.includes('walk-down') ||
            animation.key.includes('walk-up')
          ) {
            child.flipX = !child.flipX;
          }
        });
      }
    });
  }

  preDestroy() {
    this.scene.events.off('prerender', this.handlePrerender, this);
    this.scene.events.off('update', this.update, this);
    this.scene.input.off('pointerdown', this.handlePointerDown, this);
  }

  getCharacterBodyTexture() {
    const equippedItems = getCharacterInventory().equippedItems;
    const equippedComplexion = Customizations.find(
      (customization) => customization.id === equippedItems['complexion'],
    );
    return equippedComplexion?.name || 'character-body';
  }

  handlePrerender() {
    if (this.interactionEnabled) {
      this.setPosition(this.body!.x, this.body!.y);
    }
  }

  handleEquippedCosmetic(cosmetic: Customization) {
    const refreshCharacter = () => {
      this.createAnimations();

      this.updateCharacterAppearance();
      this.setVisible(false);
      this.setVisible(true);
      EventBus.emit(GAME_EVENTS.PLAYER_TEXTURES_FINISHED_REFRESH);
    };

    if (!this.scene.textures.exists(getSpriteSheetPath(cosmetic))) {
      loadCustomizationTextures(this.scene, cosmetic, () => {
        refreshCharacter();
      });
    } else {
      refreshCharacter();
    }
  }

  async handleSceneReady() {
    await waitUntil(this.scene.time, () => this.hasFinishedLoadingCosmetics);
    if (!this.scene) return; // Make sure the scene is still there once we've finished loading.

    this.setVisible(true);

    this.createAnimations();
    this.updateCharacterAppearance();

    // Setup dynamic sort order.
    new DynamicSortByY(this, this.scene);
  }

  /* END-USER-CODE */
}

/* END OF COMPILED CODE */

// You can write more code here
